import { inject, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { AbstractService, LocaleService, LocaleTools, SolSortDirection, withBearerToken } from '@sol/components';
import {
  catchError,
  delay,
  map,
  MonoTypeOperatorFunction,
  Observable,
  of,
  retry,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { AsmComponentsService } from './asm-components.service';
import { ACCESS_REASON_GATE } from './access-reason-gate';
import { STEP_UP_GATE, StepUpMethodDto } from './step-up-gate';
import { AsmError, AsmErrorType } from '../models/asm-error';
import { AsmServiceTools } from '../tools/asm-service-tools';

export interface AsmOptions {
  params?: AsmParams;
  baseUrl?: string;
  basePath?: string;
  headers?: Record<string, string>;
}

export type AsmParams = Record<
  string,
  string | number | boolean | ReadonlyArray<string | number | boolean> | undefined
>;

export type AsmParamsInternal = Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>;

export interface AsmDummyItemsOptions<D> {
  total: number;
  delayMs: number;
}

export interface AsmDummyPageOptions<A, D> extends AsmDummyItemsOptions<D> {
  toPageDto: (items: D[], total: number, page: number, pageSize: number) => A;
  page: number;
  pageSize: number;
}

export abstract class AbstractAsmService<S> extends AbstractService<S> {
  private static readonly REASON_REQUIRED_CODE = 'reason_required';
  private static readonly INSUFFICIENT_AMR_CODE = 'insufficient_amr';

  private readonly _httpClient = inject(HttpClient);
  private readonly injector = inject(Injector);
  protected readonly asmComponentsService = inject(AsmComponentsService);
  protected readonly localeService = inject(LocaleService);

  protected constructor(
    protected readonly basePath: string,
    initialState: S,
  ) {
    super(initialState);
  }

  private getUrl(options?: AsmOptions): string {
    const baseUrl = options?.baseUrl ?? this.asmComponentsService.state.apiUrl;
    const basePath = options?.basePath ?? this.basePath;
    return baseUrl + basePath;
  }

  protected httpGet<R>(endpoint: string, options?: AsmOptions): Observable<R> {
    const params = AbstractAsmService.normalizeAsmParams(options?.params);
    const path = this.getUrl(options) + endpoint;
    return this._httpClient
      .get<R>(path, {
        headers: this.httpHeaders(options?.headers),
        params,
        context: withBearerToken(),
      })
      .pipe(this.handleHttpError<R>('GET ' + path));
  }

  protected httpPatch<P, R>(endpoint: string, payload: P, options?: AsmOptions): Observable<R> {
    const params = AbstractAsmService.normalizeAsmParams(options?.params);
    const path = this.getUrl(options) + endpoint;
    return this._httpClient
      .patch<R>(path, payload, {
        headers: this.httpHeaders(options?.headers),
        params,
        context: withBearerToken(),
      })
      .pipe(this.handleHttpError<R>('PATCH ' + path));
  }

  protected httpPost<P, R>(endpoint: string, payload: P, options?: AsmOptions): Observable<R> {
    const params = AbstractAsmService.normalizeAsmParams(options?.params);
    const path = this.getUrl(options) + endpoint;
    return this._httpClient
      .post<R>(path, payload, {
        headers: this.httpHeaders(options?.headers),
        params,
        context: withBearerToken(),
      })
      .pipe(this.handleHttpError<R>('POST ' + path));
  }

  protected httpPut<P, R>(endpoint: string, payload: P, options?: AsmOptions): Observable<R> {
    const params = AbstractAsmService.normalizeAsmParams(options?.params);
    const path = this.getUrl(options) + endpoint;
    return this._httpClient
      .put<R>(path, payload, {
        headers: this.httpHeaders(options?.headers),
        params,
        context: withBearerToken(),
      })
      .pipe(this.handleHttpError<R>('PUT ' + path));
  }

  protected httpDelete<R>(endpoint: string, options?: AsmOptions, body?: unknown): Observable<R> {
    const params = AbstractAsmService.normalizeAsmParams(options?.params);
    return this._httpClient
      .delete<R>(this.getUrl(options) + endpoint, {
        headers: this.httpHeaders(options?.headers),
        params,
        body,
        context: withBearerToken(),
      })
      .pipe(this.handleHttpError<R>('DELETE ' + endpoint));
  }

  protected static toOrdering(
    columnId: string | undefined,
    direction: SolSortDirection | undefined,
  ): string | undefined {
    if (columnId === undefined || direction === undefined) {
      return undefined;
    }
    return direction === 'desc' ? `-${columnId}` : columnId;
  }

  protected httpHeaders(headers: Record<string, string> = {}): HttpHeaders {
    const state = this.authenticationService.state;
    if (!state.enabled) {
      console.error('Authentication is not enabled');
      return new HttpHeaders();
    }
    const languageId = LocaleTools.toCurrentLanguageId(this.localeService.state);
    const asmComponentsState = this.asmComponentsService.state;
    const scope = asmComponentsState.scope;
    const subscopeId = asmComponentsState.subscopeId;
    const subscopeKey = asmComponentsState.subscopeKey;
    const subscope = subscopeId ? (subscopeKey ?? subscopeId) : undefined;
    const httpHeaders = new HttpHeaders({
      'X-Version': '1.0.0', // Todo
      'X-Origin': 'platform',
      'Content-Type': 'application/json',
      'Accept-Language': languageId,
      ...headers,
    });
    const withScope = scope ? httpHeaders.set('Scope', scope) : httpHeaders.delete('Scope');
    return subscope ? withScope.set('Subscope', subscope) : withScope.delete('Subscope');
  }

  /**
   * Convenience method to quickly retrieve some paged dummy data.
   */
  protected fetchDummyPage<A, D>(
    examplesF: (id: number) => D[],
    options: Partial<AsmDummyPageOptions<A, D>> = {},
  ): Observable<A> {
    const finalOptions = {
      toPageDto: (items: D[], total: number, page: number, pageSize: number) =>
        ({ items, page: { total, number: page, size: pageSize } }) as A,
      page: 1,
      pageSize: 25,
      total: 1143,
      delayMs: 900,
      ...options,
    };
    const array: D[] = [];
    const count = Math.min(finalOptions.pageSize, finalOptions.total);
    for (let i = 0; i < count; i++) {
      const examples = examplesF(i);
      const randomIndex = Math.floor(Math.random() * examples.length);
      array.push(examples[randomIndex]);
    }
    return of(array).pipe(
      map(items => finalOptions.toPageDto(items, finalOptions.total, finalOptions.page, finalOptions.pageSize)),
      delay(finalOptions.delayMs),
    );
  }

  /**
   * Convenience method to quickly retrieve some paged dummy data.
   */
  protected fetchDummyItems<D>(
    examplesF: (id: number) => D[],
    options: Partial<AsmDummyItemsOptions<D>> = {},
  ): Observable<D[]> {
    const finalOptions = {
      delayMs: 900,
      total: Math.round(Math.random() * 25),
      ...options,
    };
    const array: D[] = [];
    const count = finalOptions.total;
    for (let i = 0; i < count; i++) {
      const examples = examplesF(i);
      const randomIndex = Math.floor(Math.random() * examples.length);
      array.push(examples[randomIndex]);
    }
    const delayMs = Math.random() * finalOptions.delayMs;
    return of(array).pipe(delay(delayMs));
  }

  /**
   * Delayed POST operation with configurable delay.
   * Common delays: 200ms (validation), 1500ms (search), 1900ms (mutations).
   */
  protected delayedPost<R>(result: R, delayMs: number = 1900): Observable<R> {
    return of(result).pipe(delay(delayMs));
  }

  /**
   * Delayed PUT operation with configurable delay.
   */
  protected delayedPut<R>(result: R, delayMs: number = 1900): Observable<R> {
    return of(result).pipe(delay(delayMs));
  }

  /**
   * Delayed PATCH operation with configurable delay.
   */
  protected delayedPatch<R>(result: R, delayMs: number = 1900): Observable<R> {
    return of(result).pipe(delay(delayMs));
  }

  /**
   * Delayed DELETE operation with configurable delay.
   */
  protected delayedDelete<R = void>(result?: R, delayMs: number = 1900): Observable<R> {
    return of(result as R).pipe(delay(delayMs));
  }

  protected handleHttpError<R>(operation: string): MonoTypeOperatorFunction<R> {
    const scopeId = this.asmComponentsService.state.scope;
    return source =>
      source.pipe(
        retry({ count: 1, delay: error => this.awaitAccessReason(error, scopeId) }),
        tap({ error: error => this.requestStepUp(error) }),
        this.asAsmError(operation),
      );
  }

  /**
   * Normalizes any thrown error to AsmError.
   *
   * Use this after mapping/parsing operators to avoid leaking raw errors to UI. Doing so puts it
   * downstream of the one the http helpers already apply, so an error it has handled once passes
   * straight through — handling a 401 twice logs the user out twice.
   */
  protected asAsmError<R>(operation: string): MonoTypeOperatorFunction<R> {
    return source =>
      source.pipe(
        catchError(error => {
          if (error instanceof AsmError) {
            return throwError(() => error);
          }
          const asmError = AsmServiceTools.toAsmErrorFromUnknown(error, operation);
          if (asmError.type === AsmErrorType.UNAUTHORIZED) {
            this.onUnauthorized(asmError);
          }
          return throwError(() => asmError);
        }),
      );
  }

  protected onUnauthorized(error: AsmError): void {
    console.warn('Unauthorized', error.toString());
    this.authenticationService.logout();
  }

  private awaitAccessReason(error: unknown, scopeId: string | undefined): Observable<void> {
    if (!scopeId || !AbstractAsmService.isReasonRequired(error)) {
      return throwError(() => error);
    }
    return this.injector
      .get(ACCESS_REASON_GATE)
      .request(scopeId)
      .pipe(switchMap(submitted => (submitted ? of(undefined) : throwError(() => error))));
  }

  private static isReasonRequired(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === 403 &&
      error.error?.code === AbstractAsmService.REASON_REQUIRED_CODE
    );
  }

  private requestStepUp(error: unknown): void {
    if (AbstractAsmService.isInsufficientAmr(error)) {
      this.injector.get(STEP_UP_GATE).request(error.error.allowed_methods);
    }
  }

  private static isInsufficientAmr(
    error: unknown,
  ): error is HttpErrorResponse & { error: { allowed_methods: StepUpMethodDto[] } } {
    return (
      error instanceof HttpErrorResponse &&
      error.status === 403 &&
      error.error?.code === AbstractAsmService.INSUFFICIENT_AMR_CODE
    );
  }

  /**
   * Removes any params that have undefined values.
   */
  private static normalizeAsmParams(params: AsmParams | undefined): AsmParamsInternal | undefined {
    if (params === undefined) {
      return undefined;
    }
    return Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined),
    ) as any as AsmParamsInternal;
  }
}
